import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './GenieAIChatWindow.scss';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { GenieService } from '../../../../services/GenieService/GenieService';
import { GenieAction, GenieChatTurn, GenieSubcategory } from '../../../../services/GenieService/genieTypes';
import { ErrorLogger } from '../../../../utils/Logger';

enum MessageKind { Text = 'text', Subcategories = 'subcategories', NoMatch = 'no_match' }

interface ChatMessage {
  id: string;
  role: 'user' | 'genie';
  text: string;
  kind: MessageKind;
  suggestions?: string[];
  subcategories?: GenieSubcategory[];
}

interface GenieAIChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
}

let messageCounter = 0;
const nextId = () => `${Date.now()}-${messageCounter++}`;

const GenieAIChatWindow: React.FC<GenieAIChatWindowProps> = ({
  isOpen,
  onClose,
  initialMessage = 'Hi Genie'
}) => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  const turnsRef = useRef<GenieChatTurn[]>([]);
  const isThinkingRef = useRef(false);
  const didInitRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking, scrollToBottom]);

  const setThinking = useCallback((value: boolean) => {
    isThinkingRef.current = value;
    setIsThinking(value);
  }, []);

  const mapKind = (action: GenieAction): MessageKind => {
    if (action === GenieAction.ShowSubcategories) return MessageKind.Subcategories;
    if (action === GenieAction.NoMatch) return MessageKind.NoMatch;
    return MessageKind.Text;
  };

  const runGenie = useCallback(async (turns: GenieChatTurn[]) => {
    setThinking(true);
    try {
      const response = await GenieService.shared.chat(turns);
      turnsRef.current = [...turns, { role: 'genie', text: response.reply }];
      setMessages(prev => [
        ...prev,
        {
          id: nextId(),
          role: 'genie',
          text: response.reply,
          kind: mapKind(response.action),
          suggestions: response.suggestions,
          subcategories: response.subcategories
        }
      ]);
    } catch (error) {
      ErrorLogger.log(error, 'GenieAIChatWindow.runGenie');
      const fallbackText =
        "Sorry, I had a hiccup just now. Could you tell me a bit more about what you're looking to get done?";
      turnsRef.current = [...turns, { role: 'genie', text: fallbackText }];
      setMessages(prev => [
        ...prev,
        {
          id: nextId(),
          role: 'genie',
          text: fallbackText,
          kind: MessageKind.Text
        }
      ]);
    } finally {
      setThinking(false);
    }
  }, [setThinking]);

  const sendMessage = useCallback((rawText: string) => {
    const text = rawText.trim();
    if (!text || isThinkingRef.current) return;

    const turns = [...turnsRef.current, { role: 'user' as const, text }];
    turnsRef.current = turns;

    setMessages(prev => [...prev, { id: nextId(), role: 'user', text, kind: MessageKind.Text }]);
    setInputValue('');
    runGenie(turns);
  }, [runGenie]);

  useEffect(() => {
    if (isOpen && !didInitRef.current) {
      didInitRef.current = true;
      turnsRef.current = [];
      setMessages([]);
      sendMessage(initialMessage);
    }
    if (!isOpen) {
      didInitRef.current = false;
      turnsRef.current = [];
      setMessages([]);
      setInputValue('');
      setThinking(false);
    }
  }, [isOpen, initialMessage, sendMessage, setThinking]);

  // Prevent background scrolling while the chat is open.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  const handleSubcategoryClick = (subcategory: GenieSubcategory) => {
    onClose();
    navigate(`/categories/subcategory/${subcategory.id}/giglist`);
  };

  const handleRequestCategory = () => {
    onClose();
    navigate('/categories#request-new-category');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendMessage(inputValue);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="genie-chat-overlay" onClick={onClose}>
      <div className="genie-chat-window" onClick={(e) => e.stopPropagation()}>
        <div className="genie-chat-header">
          <div className="genie-chat-title">
            <AutoAwesomeIcon className="genie-icon" />
            <span>GenieAI Consultant</span>
            <span className="beta-tag">BETA</span>
          </div>
          <div className="genie-chat-close" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>

        <div className="genie-chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.role === 'user' ? 'user' : 'ai'}`}>
              <div className="message-content">
                <div className="message-text">{message.text}</div>

                {message.kind === MessageKind.Text && message.suggestions && message.suggestions.length > 0 && (
                  <div className="message-suggestions">
                    {message.suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        className="suggestion-chip"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {message.kind === MessageKind.Subcategories && message.subcategories && message.subcategories.length > 0 && (
                  <div className="genie-subcategory-carousel">
                    {message.subcategories.map((subcategory) => (
                      <button
                        key={subcategory.id}
                        className="genie-subcategory-card"
                        onClick={() => handleSubcategoryClick(subcategory)}
                      >
                        <div className="genie-subcategory-image">
                          <img src={subcategory.icon} alt={subcategory.name} loading="lazy" />
                        </div>
                        <span className="genie-subcategory-name">{subcategory.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {message.kind === MessageKind.NoMatch && (
                  <div className="genie-nomatch">
                    <button className="genie-request-button" onClick={handleRequestCategory}>
                      Request this category
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isThinking && (
            <div className="message ai">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="genie-chat-input">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="genie-input"
            disabled={isThinking}
          />
          <button
            onClick={() => sendMessage(inputValue)}
            disabled={!inputValue.trim() || isThinking}
            className="genie-send-button"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenieAIChatWindow;
